import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import { alertActions } from '../redux/AlertController';

const OwnerShopDetails = () => {
    const [batches, setBatches] = useState([]);
    const [qrCount, setQrCount] = useState(10);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [selectedPet, setSelectedPet] = useState(null);
    const [pets, setPets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingPets, setIsLoadingPets] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const token = JSON.parse(localStorage.getItem('token'));
    const dispatch = useDispatch();
    const nextBatchNumber = batches.length + 1;

    const loadBatches = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/owner/batches`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const json = await res.json();
            if (res.ok) {
                setBatches(json);
            } else {
                dispatch(alertActions.showAlert({ msg: json.message || 'تعذر تحميل الدفعات', type: 'error' }));
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message || 'حدث خطأ أثناء تحميل الدفعات', type: 'error' }));
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, token]);

    useEffect(() => {
        loadBatches();
    }, [loadBatches]);

    const createBatch = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/owner/batches`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ batchName: String(nextBatchNumber), qrCount: Number(qrCount) })
            });
            const json = await res.json();

            if (res.ok) {
                setQrCount(10);
                setShowCreateModal(false);
                dispatch(alertActions.showAlert({ msg: 'تم إنشاء الدفعة وتوليد أكواد QR بنجاح', type: 'success' }));
                loadBatches();
            } else {
                dispatch(alertActions.showAlert({ msg: json.message || 'تعذر إنشاء الدفعة', type: 'error' }));
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message || 'حدث خطأ أثناء إنشاء الدفعة', type: 'error' }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const loadBatchPets = async (batch) => {
        setSelectedBatch(batch);
        setSelectedPet(null);
        setShowBatchModal(true);
        setIsLoadingPets(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/owner/batches/${batch._id}/pets`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const json = await res.json();
            if (res.ok) {
                setPets(json);
            } else {
                dispatch(alertActions.showAlert({ msg: json.message || 'تعذر تحميل اكواد هذه الدفعة', type: 'error' }));
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message || 'حدث خطأ أثناء تحميل الأكواد', type: 'error' }));
        } finally {
            setIsLoadingPets(false);
        }
    };

    const closeBatchModal = () => {
        setShowBatchModal(false);
        setSelectedBatch(null);
        setSelectedPet(null);
        setPets([]);
    };

    const downloadBatchQRCodes = async () => {
        if (!selectedBatch || pets.length === 0) {
            dispatch(alertActions.showAlert({ msg: 'لا يوجد أكواد لتحميلها', type: 'warrning' }));
            return;
        }

        setIsDownloading(true);
        try {
            const rows = pets.map((pet) => ({
                URL: `https://refq-pets.ly/${pet._id}`
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, `Batch-${selectedBatch.batchName}`);

            XLSX.writeFile(workbook, `batch-${selectedBatch.batchName}-docs.xlsx`);

            dispatch(alertActions.showAlert({ msg: 'تم تجهيز ملف Excel للتحميل', type: 'success' }));
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message || 'حدث خطأ أثناء تجهيز ملف التحميل', type: 'error' }));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <section className="page-content">
            <div className="shops-toolbar">
                <h2>دفعات الوثائق</h2>
                <button className="add-shop-btn" type="button" onClick={() => setShowCreateModal(true)}>
                    <FiPlus />
                    <span>إنشاء دفعة جديدة</span>
                </button>
            </div>

            <div className="card">
                <div className="batch-cards">
                    {isLoading && <p className="empty">جاري تحميل الدفعات...</p>}
                    {batches.map((batch) => (
                        <div className="batch-card" key={batch._id}>
                            <div className="batch-card-main">
                                <strong className="batch-number">دفعة #{batch.batchName}</strong>
                                <p>عدد الأكواد: {batch.qrCount}</p>
                                <p>تاريخ الإنشاء: {new Date(batch.createdAt).toLocaleDateString()}</p>
                            </div>
                            <button type="button" onClick={() => loadBatchPets(batch)} disabled={isSubmitting}>عرض</button>
                        </div>
                    ))}
                    {!isLoading && batches.length === 0 && <p className="empty">لا يوجد دفعات</p>}
                </div>
            </div>

            {showCreateModal && (
                <div className="confirm-overlay">
                    <div className="confirm-box create-shop-modal">
                        <h3>توليد دفعة QR جديدة</h3>
                        <form className="create-shop-form" onSubmit={createBatch}>
                            <input
                                type="text"
                                value={`رقم الدفعة: ${nextBatchNumber}`}
                                readOnly
                            />
                            <input
                                type="number"
                                min="1"
                                value={qrCount}
                                onChange={(e) => setQrCount(e.target.value)}
                                placeholder="عدد الأكواد"
                                required
                            />
                            <div className="actions">
                                <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'جاري الإنشاء...' : 'إنشاء الدفعة'}</button>
                                <button type="button" onClick={() => setShowCreateModal(false)} disabled={isSubmitting}>إلغاء</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showBatchModal && selectedBatch && (
                <div className="confirm-overlay">
                    <div className="confirm-box qr-table-modal">
                        {!selectedPet ? (
                            <>
                                <h3>دفعة #{selectedBatch.batchName}</h3>
                                {isLoadingPets && <p className="empty">جاري تحميل الأكواد...</p>}
                                {!isLoadingPets && pets.length === 0 && <p className="empty">لا يوجد أكواد في هذه الدفعة</p>}
                                {!isLoadingPets && pets.length > 0 && (
                                    <div className="table-wrapper">
                                        <table className="qr-table">
                                            <thead>
                                                <tr>
                                                    <th>الرقم التسلسلي</th>
                                                    <th>هاتف المالك</th>
                                                    <th>الحالة</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pets.map((pet, index) => (
                                                    <tr key={pet._id}>
                                                        <td>
                                                            <button type="button" className="table-link-btn" onClick={() => setSelectedPet(pet)}>
                                                                {pet.number || index + 1}
                                                            </button>
                                                        </td>
                                                        <td>{pet.primaryPhone || '---'}</td>
                                                        <td>{pet.isActive ? 'مفعّل' : 'غير مفعّل'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="actions">
                                    <button type="button" onClick={downloadBatchQRCodes} disabled={isDownloading || isLoadingPets || pets.length === 0}>
                                        {isDownloading ? 'جاري التجهيز...' : 'Download Excel'}
                                    </button>
                                    <button type="button" onClick={closeBatchModal}>إغلاق</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3>تفاصيل الكود #{selectedPet.number || 1}</h3>
                                <div className="pet-details-view">
                                    <p><strong>الرقم التسلسلي:</strong> {selectedPet.number || '---'}</p>
                                    <p><strong>QR Code:</strong> {selectedPet.qrCode || '---'}</p>
                                    <p><strong>هاتف المالك:</strong> {selectedPet.primaryPhone || '---'}</p>
                                    <p><strong>الحالة:</strong> {selectedPet.isActive ? 'مفعّل' : 'غير مفعّل'}</p>
                                    <p><strong>اسم المالك:</strong> {selectedPet.ownerName || '---'}</p>
                                    <p><strong>اسم الحيوان:</strong> {selectedPet.petName || '---'}</p>
                                </div>
                                <div className="actions">
                                    <button type="button" onClick={() => setSelectedPet(null)}>رجوع</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default OwnerShopDetails;
